import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

interface VerificationInfo {
  token: string;
  filename: string;
  content: string;
  instructions: string;
  // The site this account claims. Optional so an older API (or a mock that
  // predates the field) degrades to the generic wording rather than printing
  // "undefined" into the URL the publisher is about to copy.
  websiteUrl?: string | null;
}

// The origin the file has to be reachable at. `website_url` may be a deep post
// URL on an audit-born account, so resolve to the ORIGIN — the same thing
// verificationProbeUrls tries first, which is what makes this the URL that
// will actually be fetched rather than a plausible-looking one.
function verificationOrigin(websiteUrl?: string | null): string | null {
  if (!websiteUrl) return null;
  try {
    return new URL(websiteUrl).origin;
  } catch {
    return null;
  }
}

const WebsiteVerification: React.FC = () => {
  const { verificationStatus, refreshProfile } = useAuth();
  const [info, setInfo] = useState<VerificationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [resultMessage, setResultMessage] = useState<{ success: boolean; text: string } | null>(null);

  useEffect(() => {
    if (verificationStatus === 'verified') {
      setLoading(false);
      return;
    }
    apiService.getVerificationInfo()
      .then((data) => setInfo(data.verification))
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, [verificationStatus]);

  const handleDownload = useCallback(() => {
    if (!info) return;
    const blob = new Blob([info.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = info.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoking synchronously races the browser's async fetch of the blob URL
    // and silently aborts the download; defer it well past the click
    window.setTimeout(() => URL.revokeObjectURL(url), 10000);
  }, [info]);

  const [copied, setCopied] = useState(false);
  const handleCopyContent = useCallback(async () => {
    if (!info) return;
    try {
      await navigator.clipboard.writeText(info.content);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = info.content;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [info]);

  const handleVerify = useCallback(async () => {
    setVerifying(true);
    setResultMessage(null);
    try {
      const result = await apiService.verifyWebsite();
      setResultMessage({ success: result.success, text: result.message });
      if (result.success) {
        await refreshProfile();
      }
    } catch (err) {
      setResultMessage({ success: false, text: err instanceof Error ? err.message : 'Verification failed' });
    } finally {
      setVerifying(false);
    }
  }, [refreshProfile]);

  // Completed states disappear (portal-ux-clarity): once verified, no
  // instructions and no confirmation panel — the header badge owns the state.
  if (verificationStatus === 'verified') {
    return null;
  }

  if (loading) {
    return <section className="verification-section"><p>Loading verification info...</p></section>;
  }

  const origin = verificationOrigin(info?.websiteUrl);
  const siteLabel = origin ? origin.replace(/^https?:\/\//, '') : 'the site you control';
  const verifyUrl = origin
    ? `${origin}/${info?.filename ?? ''}`
    : `https://yoursite.com/${info?.filename ?? ''}`;

  return (
    <section className="verification-section" data-testid="verification-section">
      <h2>Verify the site before serving matches</h2>
      <div className="verification-content">
        {info ? (
          <>
            {/* The opener used to be "The public audit did not prove
                ownership." — unconditional, so a publisher who signed up
                directly was told a step they never took had failed. The
                audit-born path already gets that framing on the report page
                itself, at the moment it is true; here the neutral sentence is
                correct for everyone. */}
            <p>Add this file to {siteLabel} to unlock product-feed uploads and live matching:</p>
            <ol>
              <li>Create a file named <code>{info.filename}</code> with the content below (or use the Download button)</li>
              <li>Upload it to your website's root directory</li>
              <li>Ensure it's reachable at: <code>{verifyUrl}</code></li>
              <li>Click the "Verify Website" button below</li>
            </ol>
            <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
              <pre
                data-testid="verification-file-content"
                style={{
                  padding: '0.9rem',
                  paddingRight: '5.5rem',
                  backgroundColor: 'var(--surface-alt)',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--radius)',
                  fontSize: '0.82rem',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >{info.content}</pre>
              <button
                data-testid="copy-verification-content"
                onClick={handleCopyContent}
                className="btn btn--secondary"
                style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="verification-actions compact-action-row" style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button
                data-testid="verify-website-btn"
                className="verify-btn"
                onClick={handleVerify}
                disabled={verifying}
              >
                {verifying ? 'Verifying...' : 'Verify Website'}
              </button>
              <button
                data-testid="download-verification-btn"
                className="download-btn"
                onClick={handleDownload}

              >
                Download Verification File
              </button>
            </div>
          </>
        ) : (
          <p>Unable to load verification info. Please try again later.</p>
        )}

        {resultMessage && (
          <div
            data-testid="verification-result"
            style={{
              marginTop: '1rem',
              padding: '1rem',
              backgroundColor: resultMessage.success ? 'var(--good-bg)' : 'var(--danger-bg)',
              borderRadius: 'var(--radius)',
              color: resultMessage.success ? 'var(--good)' : 'var(--danger)',
            }}
          >
            {resultMessage.text}
          </div>
        )}
      </div>
    </section>
  );
};

export default WebsiteVerification;
